import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, DatePicker, Form, Input, TimePicker } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import {
  createAppointment,
  getAppointmentById,
  getPackageById,
  getPackages,
  updateAppointment,
  getTreatmentTypes,
  getMissedAppointments,
  getAllSubsidiaries,
  getConfigs,
  createExternalAppointment,
} from '../../../../appRedux/actions';
import {
  appointmentConst,
  buttonLabel,
  cardConfig,
  dateFormat,
  dateFormatMx,
  formConfig,
  messages,
  optionDictionary,
  placeHolders,
  timeFormat,
  UrlPaths,
} from '../../../../constants';
import {
  getLocalRedemptionBranchAuthorization,
  getRedemptionBranchAuthorization,
} from '../../../../util';
import {
  agendaConst,
  agendaOptionsConst,
  appointmentMethodConst,
} from '../../../../constants/Agenda.const';
import { packageConst } from '../../../../constants/Package.const';
import { CancelButton, CustomSelect } from '../../../uiComponents';
import moment from 'moment/moment';
import { getPurchasedProductsByCustomer } from '../../../../appRedux/actions/PurchasedProduct';
import { checkIfWithin24Hours } from '../../../../util/checkIfWithin24Hours';
import { CustomerSearchSelect } from '../../../uiComponents/SelectCustomer';
import { useAppointmentModals } from './JoinComponent/useAppointmentModals';
import { useCabains } from './JoinComponent/useCabains';
import { CabainSelect } from './JoinComponent/CabainSelect';
import { TreatmentOptionSection } from './JoinComponent/TreatmentOptionSection';
import { useCustomerValidations } from './JoinComponent/useCustomerValidations';
import { useAppointmentCabainSync } from './JoinComponent/useAppointmentCabainSync';
import { useAppointmentFormPrefill } from './JoinComponent/useAppointmentFormPrefill';
import { useAppointmentFormEdit } from './JoinComponent/useAppointmentFormEdit';
import { getScheduledTreatmentName } from '../../../../util/getScheduledTreatmentName';
import { AppointmentModals } from './JoinComponent/AppointmentModals';
import { buildAppointmentPayload } from '../../../../util/mappers/appointmentPayload';
import { RedemptionBranchAuthorizationModal } from '../../RedemptionBranchAuthorizationModal';

export const AppointmentForm = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const { role } = useSelector((state) => state.userAuth);
  const { customers } = useSelector((state) => state.customer);
  const { packages } = useSelector((state) => state.packages);
  const { treatmentTypes } = useSelector((state) => state.treatmentType);
  const { appointmentData } = useSelector((state) => state.generalStates);
  const { appointmentById } = useSelector((state) => state.agenda);
  const { missedAppointments } = useSelector(
    (state) => state.missedAppointment
  );
  const [response, setResponse] = useState();
  const [date, setDate] = useState();
  const [startTime, setStartTime] = useState();
  const [option, setOption] = useState(agendaConst.promotion);
  const [packageId, setPackageId] = useState();
  const [promotionId, setPromotionId] = useState();
  const { packageById } = useSelector((state) => state.packages);
  const { allSubsidiaries: subsidiaries } = useSelector(
    (state) => state.subsidiary
  );
  const branchId = appointmentData?.branchId;
  const [term, setTerm] = useState();
  const [isBefore, setIsBefore] = useState(false);
  const [isScheduled, setInScheduled] = useState(true);
  const [customerId, setCustomerId] = useState();
  const [purchasedProducts, setPurchasedProduct] = useState([]);
  const [treatmentPackage, setTreatmentPackage] = useState([]);
  const [remainingSessions, setRemainingSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authorizationDetail, setAuthorizationDetail] = useState(null);
  const [pendingAppointment, setPendingAppointment] = useState(null);
  const { customerPurchasedProducts } = useSelector((state) => state.customer);
  const [isEditable, setIsEditable] = useState(true);
  const [isInProcess, setIsInProcess] = useState(false);
  const [letOption, setLetOption] = useState(() => !id);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [customerConfirmedEdit, setCustomerConfirmedEdit] = useState();
  // Candado inmediato contra doble submit antes de que React alcance a re-renderizar el loading.
  const isSubmittingRef = useRef(false);
  const promos = useMemo(
    () => packages?.filter((value) => value.type === packageConst.promotion),
    [packages]
  );
  const packs = useMemo(
    () => packages?.filter((value) => value.type === packageConst.package),
    [packages]
  );

  const customer = useMemo(
    () => customers?.find((customer) => customer.id === customerId),
    [customers, customerId]
  );

  const treatmentScheduled = useMemo(
    () =>
      `${appointmentById?.purchasedProduct?.currentSession ?? 0}/${appointmentById?.purchasedProduct?.sessions ?? 0} - ${getScheduledTreatmentName(appointmentById)}`,
    [appointmentById]
  );
  const createExternalAppointments = state?.fromPopover;

  const {
    cabains,
    treatmentTypeId,
    setTreatmentTypeId,
    onCabainChange,
    getCabainOptions,
  } = useCabains(branchId, form);

  const resetFormState = useCallback(() => {
    form.resetFields();

    setOption(agendaConst.promotion);

    form.setFieldsValue({
      appointmentOption: agendaConst.promotion,
      treatmentCategory: [''],
    });

    setResponse(undefined);
    setDate(undefined);
    setStartTime(undefined);
    setPackageId(undefined);
    setPromotionId(undefined);
    setTerm(undefined);
    setIsBefore(false);
    setInScheduled(true);
    setCustomerId(undefined);
    setPurchasedProduct([]);
    setTreatmentPackage([]);
    setRemainingSessions([]);
    setIsLoading(false);
    setIsEditable(true);
    setIsInProcess(false);
    setLetOption(!id);
    setIsNotesModalOpen(false);
    setCustomerConfirmedEdit(undefined);
    setTreatmentTypeId(undefined);
  }, [form, id, setTreatmentTypeId]);

  useEffect(() => {
    resetFormState();
  }, [id, resetFormState]);

  const filteredTreatmentPackage = useMemo(() => {
    if (!treatmentTypeId) return [];

    return treatmentPackage?.filter(
      (tp) => tp?.treatment?.treatmentType?.id === treatmentTypeId
    );
  }, [treatmentTypeId, treatmentPackage]);

  const { openModal, showConfirm, isModalOpen, confirmType } =
    useAppointmentModals({
      navigate,
      customerId,
      form,
      setCustomerConfirmedEdit,
      setIsNotesModalOpen,
    });

  useEffect(() => {
    if (customerId) {
      dispatch(
        getPurchasedProductsByCustomer(customerId, {
          isExpired: true,
        })
      );
    }
  }, [customerId, dispatch]);

  useEffect(() => {
    dispatch(getConfigs());
  }, [dispatch]);

  useAppointmentCabainSync({
    appointmentById,
    form,
    cabains,
    onCabainChange,
  });

  useAppointmentFormPrefill({
    id,
    appointmentData,
    appointmentById,
    form,
    onCabainChange,
    setDate,
    setStartTime,
    isEditable,
  });

  useEffect(() => {
    if (id) dispatch(getAppointmentById(id));

    dispatch(
      getAllSubsidiaries({
        status: '1',
        agenda: '1',
      })
    );

    dispatch(getTreatmentTypes());
  }, [id, dispatch]);

  useEffect(() => {
    if (option && branchId) {
      dispatch(
        getPackages({
          status: '1',
          withDeleted: 'false',
          branchesIds: [branchId],
          treatmentTypeId,
          getMixed: true,
        })
      );
    }
  }, [dispatch, option, branchId, treatmentTypeId]);

  useEffect(() => {
    if (packageId) dispatch(getPackageById(packageId));
  }, [packageId, dispatch]);

  useEffect(() => {
    if (promotionId) dispatch(getPackageById(promotionId));
  }, [promotionId, dispatch]);

  useEffect(() => {
    if ([200, 201].includes(response)) {
      navigate('/dashboard/agenda');
    }
  }, [navigate, response]);

  useAppointmentFormEdit({
    id,
    appointmentById,
    form,
    setOption,
    setPackageId,
    setPromotionId,
    setDate,
    setStartTime,
    setTerm,
    setTreatmentTypeId,
    setIsBefore,
    setIsScheduled: setInScheduled,
    setIsEditable,
    setIsInProcess,
    isEditable,
  });

  const { showAlert, disabledButton, haveNotes, message, description, type } = useCustomerValidations({
    customer,
    role,
    option,
    treatmentTypeId,
    appointmentById,
    openModal,
  });

  useEffect(() => {
    if (!packageId && !promotionId) {
      setTreatmentPackage([]);
      return;
    }

    if (packageById && treatmentTypeId) {
      const treatments = packageById?.treatmentPackage?.filter(
        (treatmentPackage) => {
          return (
            treatmentPackage?.treatment?.treatmentType?.id === treatmentTypeId
          );
        }
      );

      setTreatmentPackage(treatments);
    }
  }, [packageById, treatmentTypeId, packageId, promotionId]);

  useEffect(() => {
    if (appointmentById)
      dispatch(getMissedAppointments(appointmentById?.purchasedProduct?.id));
  }, [dispatch, appointmentById]);

  useEffect(() => {
    if (missedAppointments?.length >= 2 && !isModalOpen) {
      openModal(appointmentConst.MISSED_APPOINTMENTS_ALERT_TYPE);
    }
  }, [missedAppointments, isModalOpen, openModal]);

  useEffect(() => {
    const products = customerPurchasedProducts?.filter(
      ({ treatment }) => treatment?.treatmentType?.id === treatmentTypeId
    );

    setPurchasedProduct(products);
  }, [customerPurchasedProducts, treatmentTypeId]);

  useEffect(() => {
    if (confirmType && isModalOpen) {
      showConfirm();
    }
  }, [confirmType, isModalOpen, showConfirm]);

  const onOptionChange = useCallback((value) => {
    setOption(value);
    setPackageId(null);
    setPromotionId(null);
    form.setFieldsValue({
      treatmentId: '',
      packageId: '',
      promoId: '',
      purchasedProductId: '',
      treatmentCategory: [''],
    });
  }, [form]);

  const onPurchasedProductChange = useCallback((_, purchasedProduct) => {
    const remaining = [];
    const sessions = purchasedProduct?.sessions ?? 0;
    const currentSession = purchasedProduct?.currentSession ?? 0;
    const remainingCount = Math.max(0, sessions - currentSession);

    for (let index = 1; index <= remainingCount; index++) {
      remaining.push({
        value: index,
        label: index,
      });
    }

    setRemainingSessions(remaining);
  }, []);

  const dateFormats = useCallback((value) => {
    if (!value) {
      setDate(undefined);
      return;
    }

    setDate(moment(value).format(dateFormat));
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      // Evita doble click o dos onFinish consecutivos creando/editando la misma cita.
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;
      setIsLoading(true);

      try {
        const data = buildAppointmentPayload({
          values,
          id,
          option,
          date,
          startTime,
          term,
          appointmentById,
          customerConfirmedEdit,
          cabains,
        });

        const localAuthorization = getLocalRedemptionBranchAuthorization({
          appointment: data,
          purchasedProducts,
          branches: subsidiaries,
        });
        if (localAuthorization) {
          setAuthorizationDetail(localAuthorization);
          setPendingAppointment(data);
          return;
        }

        let requestResponse;
        if (id) {
          requestResponse = await dispatch(updateAppointment(id, data));
        } else {
          if (createExternalAppointments) {
            requestResponse = await dispatch(createExternalAppointment(data));
          } else {
            requestResponse = await dispatch(createAppointment(data));
          }
        }

        const authorization = getRedemptionBranchAuthorization(requestResponse);
        if (authorization) {
          setAuthorizationDetail(authorization);
          setPendingAppointment(data);
        } else {
          setResponse(requestResponse);
        }
      } finally {
        setIsLoading(false);
        setCustomerConfirmedEdit(false);
        isSubmittingRef.current = false;
      }
    },
    [
      id,
      option,
      date,
      startTime,
      term,
      appointmentById,
      customerConfirmedEdit,
      cabains,
      purchasedProducts,
      subsidiaries,
      dispatch,
      createExternalAppointments,
    ]
  );

  const within24HoursValidation = useCallback(
    async (values) => {
      const isWithin24Hours = checkIfWithin24Hours(
        appointmentById?.date,
        appointmentById?.startTime
      );

      if (id && isWithin24Hours && customerConfirmedEdit === undefined) {
        openModal(appointmentConst.EDIT_WITHIN_24H_ALERT_TYPE);
        return;
      }

      await handleSubmit(values);
    },
    [
      id,
      appointmentById?.date,
      appointmentById?.startTime,
      customerConfirmedEdit,
      handleSubmit,
      openModal,
    ]
  );

  const handleNotes = useCallback(() => {
    setIsNotesModalOpen(true);
  }, [setIsNotesModalOpen]);

  return (
    <>
      <Card size="small" bordered={true} style={cardConfig}>
        {showAlert && type && (
          <Alert
            message={message}
            description={description}
            type={type}
            showIcon
            closable
          />
        )}

        <Form
          disabled={
            (isBefore && appointmentById) ||
            (!isEditable && !appointmentById) ||
            (!isScheduled && !appointmentById)
          }
          {...formConfig}
          initialValues={{
            treatmentCategory: [''],
            appointmentOption: option,
          }}
          autoComplete="off"
          onFinish={within24HoursValidation}
          onFinishFailed={(errorInfo) =>
            console.log('[AppointmentForm] onFinishFailed', errorInfo)
          }
          form={form}
        >
          {id && (
            <Form.Item label="Número de cita" name="appointmentNumber">
              <Input disabled={true} />
            </Form.Item>
          )}

          <Form.Item
            label="Sucursal"
            name="branchId"
            rules={[
              {
                required: true,
                message: messages.required('Sucursal'),
              },
            ]}
          >
            <CustomSelect
              placeholder={placeHolders.branch}
              disabled={true}
              options={subsidiaries?.map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
            ></CustomSelect>
          </Form.Item>

          <CabainSelect
            cabains={cabains}
            id={id}
            treatmentTypeId={treatmentTypeId}
            isScheduled={isScheduled}
            onCabainChange={onCabainChange}
            getCabainOptions={getCabainOptions}
          />

          <Form.Item
            name="treatmentTypeId"
            label="Tipo de cita"
            rules={[
              {
                required: true,
                message: messages.required('Tipo de cita'),
              },
            ]}
          >
            <CustomSelect
              placeholder={placeHolders.appointmentType}
              onChange={setTreatmentTypeId}
              disabled={true}
              options={treatmentTypes?.map(({ id, appointmentName }) => ({
                value: id,
                label: appointmentName,
              }))}
            ></CustomSelect>
          </Form.Item>

          <>
            {!id && (
              <Form.Item
                rules={[
                  { required: true, message: messages.required('Paciente') },
                ]}
                name="customerId"
                label="Paciente"
                style={{
                  marginBottom: haveNotes ? 0 : '20px',
                  display: id ? 'none' : 'block',
                }}
              >
                <CustomerSearchSelect
                  onChange={(val) => {
                    setCustomerId(val);
                  }}
                />
              </Form.Item>
            )}
            {id && (
              <Form.Item
                rules={[
                  { required: true, message: messages.required('Paciente') },
                ]}
                name="customerName"
                label="Paciente"
                style={{
                  marginBottom: haveNotes ? 0 : '20px',
                  display: id ? 'block' : 'none',
                }}
              >
                <Input disabled />
              </Form.Item>
            )}
            {haveNotes && (
              <Form.Item label=" " colon={false}>
                <Button
                  type="link"
                  style={{ margin: 0, padding: 0 }}
                  onClick={handleNotes}
                >
                  {appointmentConst.NOTES}
                </Button>
              </Form.Item>
            )}
          </>

          <Form.Item
            name="date"
            label="Fecha de cita"
            rules={[
              {
                required: true,
                message: messages.required('Fecha de cita'),
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format={dateFormatMx}
              disabled={!isScheduled}
              onChange={(object, dateString) => dateFormats(object, dateString)}
              disabledDate={(current) => {
                let customDate = moment().format(dateFormat);
                return current && current < moment(customDate, dateFormat);
              }}
            />
          </Form.Item>

          <Form.Item
            name="startTime"
            label="Hora de la cita"
            rules={[
              { required: true, message: messages.required('Hora de la cita') },
            ]}
          >
            <TimePicker
              format={timeFormat}
              minuteStep={30}
              disabled={!isScheduled}
              onChange={(_, timeString) => setStartTime(timeString)}
            />
          </Form.Item>

          <Form.Item name="term" label="Duración">
            <TimePicker
              disabled={!isScheduled}
              format={timeFormat}
              minuteStep={30}
              onChange={(_, timeString) => setTerm(timeString)}
            />
          </Form.Item>

          <Form.Item
            name="appointmentMethod"
            label="Método de cita"
            rules={[
              {
                required: true,
                message: messages.required('Método de cita'),
              },
            ]}
          >
            <CustomSelect
              disabled={!isScheduled}
              placeholder={placeHolders.appointmentMethod}
              options={appointmentMethodConst}
            ></CustomSelect>
          </Form.Item>

          <Form.Item name="observations" label="Observaciones">
            <TextArea disabled={isBefore || !isScheduled || isInProcess} />
          </Form.Item>

          {letOption === false && (
            <>
              <Form.Item label="Opción">
                <Input
                  value={
                    appointmentById
                      ? `${optionDictionary[appointmentById.appointmentOption]}`
                      : ''
                  }
                  disabled
                />
              </Form.Item>

              {appointmentById?.appointmentOption ===
              agendaConst.purchasedProducts ? (
                <>
                  <Form.Item label="Tratamiento agendado">
                    <Input
                      value={treatmentScheduled}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item label="Sesiones">
                    <Input
                      value={`${appointmentById?.purchasedProduct?.currentSession ?? 0}`}
                      disabled
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item label="Categoría">
                    <Input
                      value={
                        appointmentById
                          ? appointmentById?.category?.name ||
                            (Array.isArray(appointmentById?.categories) &&
                            appointmentById.categories.length > 0
                              ? appointmentById.categories[0]?.name
                              : appointmentById?.pendingProduct?.pkg?.name)
                          : ''
                      }
                      disabled
                    />
                  </Form.Item>

                  <Form.Item label="Tratamiento agendado">
                    <Input
                      value={getScheduledTreatmentName(appointmentById)}
                      disabled
                    />
                  </Form.Item>
                </>
              )}

              <Form.Item label=" " colon={false}>
                <div style={{ all: 'unset' }}>
                  <Button htmlType="button" onClick={() => setLetOption(true)}>
                    {buttonLabel.selectDifferent}
                  </Button>
                </div>
              </Form.Item>
            </>
          )}

          {letOption === true && (
            <Form.Item
              label="Opciones"
              name="appointmentOption"
              rules={[
                { required: true, message: messages.required('Opciones') },
              ]}
            >
              <CustomSelect
                onChange={onOptionChange}
                // Sólo permite cambiar la opción cuando la cita todavía es editable.
                disabled={!isEditable}
                placeholder={placeHolders.agendaOptions}
                options={agendaOptionsConst}
              ></CustomSelect>
            </Form.Item>
          )}

          <TreatmentOptionSection
            letOption={letOption}
            option={option}
            promos={promos}
            packs={packs}
            treatmentPackage={filteredTreatmentPackage}
            purchasedProducts={purchasedProducts}
            appointmentById={appointmentById}
            remainingSessions={remainingSessions}
            onPurchasedProductChange={onPurchasedProductChange}
            setPackageId={setPackageId}
            setPromotionId={setPromotionId}
            form={form}
            treatmentTypeId={treatmentTypeId}
          />

          <Button
            type="primary"
            htmlType="submit"
            className="btn-form"
            // También deshabilita mientras se envía para reforzar el bloqueo visual del botón.
            disabled={disabledButton || isLoading}
            loading={isLoading}
          >
            {buttonLabel.save}
          </Button>
        </Form>
        <CancelButton path={UrlPaths.AGENDA} />
      </Card>
      <AppointmentModals
        isNotesModalOpen={isNotesModalOpen}
        setIsNotesModalOpen={setIsNotesModalOpen}
        customerId={customerId}
      />
      <RedemptionBranchAuthorizationModal
        open={!!authorizationDetail}
        detail={authorizationDetail}
        loading={isLoading}
        onCancel={() => {
          setAuthorizationDetail(null);
          setPendingAppointment(null);
        }}
        onConfirm={async (authorization) => {
          setIsLoading(true);
          try {
            const data = { ...pendingAppointment, ...authorization };
            const result = id
              ? await dispatch(updateAppointment(id, data))
              : createExternalAppointments
              ? await dispatch(createExternalAppointment(data))
              : await dispatch(createAppointment(data));
            if ([200, 201].includes(result)) {
              setAuthorizationDetail(null);
              setPendingAppointment(null);
              setResponse(result);
              return true;
            }
            return false;
          } finally {
            setIsLoading(false);
          }
        }}
      />
    </>
  );
};
